import { FormEvent, useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { LabeledCheckbox } from "../../components/LabeledCheckbox";
import { Combobox } from "../../components/ui/combobox";
import { toast } from "sonner";
import { useCompany } from "../contexts/CompanyContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";
import { ChevronLeft } from "../../components/icons";
import { formatCpf, formatCnpj, isValidCpf, isValidCnpj, isValidEmail } from "../utils/document";
import { navigate } from "../../lib/router";

export default function EditCompanyPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].editCompanyPage;
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [docType, setDocType] = useState<"cpf" | "cnpj">("cnpj");
  const [document, setDocument] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [requireEvolution, setRequireEvolution] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setSidebarTitle } = useCompany();
  const docTypeOptions: Array<{ value: "cpf" | "cnpj"; label: string }> = [
    { value: "cpf", label: t.fields.cpf },
    { value: "cnpj", label: t.fields.cnpj },
  ];

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      const res = await api.getCompany();
      if (!isMounted) {
        return;
      }
      if (res.ok) {
        const c = res.data;
        setName(c.name || "");
        setLegalName(c.legal_name || "");
        if (c.cnpj) {
          setDocType("cnpj");
          setDocument(formatCnpj(c.cnpj));
        } else if (c.cpf) {
          setDocType("cpf");
          setDocument(formatCpf(c.cpf));
        }
        setPhone(c.phone || "");
        setWebsite(c.website || "");
        setEmail(c.email || "");
        setAddress(c.address || "");
        setRequireEvolution(c.require_evolution !== false);
      } else {
        toast.error(res.error);
      }
      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const digits = document.replace(/\D/g, "");
    if (docType === "cpf" && digits && !isValidCpf(digits)) {
      toast.error(t.errors.invalidCpf);
      return;
    }
    if (docType === "cnpj" && digits && !isValidCnpj(digits)) {
      toast.error(t.errors.invalidCnpj);
      return;
    }
    const emailClean = email.trim().toLowerCase();
    if (!emailClean) {
      toast.error(t.errors.emailRequired);
      return;
    }
    if (!isValidEmail(emailClean)) {
      toast.error(t.errors.invalidEmail);
      return;
    }
    setSaving(true);
    const res = await api.updateCompany({
      name: name.trim(),
      legal_name: legalName.trim() || null,
      cnpj: docType === "cnpj" ? digits || null : null,
      cpf: docType === "cpf" ? digits || null : null,
      phone: phone.trim() || null,
      website: website.trim() || null,
      email: emailClean,
      address: address.trim() || null,
      require_evolution: requireEvolution,
    });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    setSidebarTitle(name.trim() || null);
    navigate("/app/settings");
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t.back}
          onClick={() => navigate("/app/settings")}
          icon={<ChevronLeft className="h-5 w-5" />}
        />
        <h1 className="text-2xl font-semibold">{t.title}</h1>
      </div>

      <div className="rounded-lg border bg-white p-6 shadow-sm">
        {loading ? (
          <div className="text-sm text-neutral-600">{t.loading}</div>
        ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>{t.fields.name}</Label>
                <Input value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div>
                <Label>{t.fields.legalName}</Label>
                <Input
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                />
              </div>
              <div className="md:max-w-xs">
                <Label>{t.fields.documentType}</Label>
                <Combobox
                  options={docTypeOptions}
                  value={docType}
                  onChange={(value) => {
                    setDocType(value as "cpf" | "cnpj");
                    setDocument("");
                  }}
                  placeholder={t.fields.documentType}
                  searchPlaceholder={t.comboboxSearch}
                  emptyMessage={t.comboboxEmpty}
                  ariaLabel={t.fields.documentType}
                  className="w-full md:w-40"
                />
              </div>
              <div>
                <Label>{t.fields.document}</Label>
                <Input
                  value={document}
                  inputMode="numeric"
                  onChange={(event) =>
                    setDocument(
                      docType === "cpf"
                        ? formatCpf(event.target.value)
                        : formatCnpj(event.target.value)
                    )
                  }
                />
              </div>
              <div>
                <Label>{t.fields.phone}</Label>
                <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
              </div>
              <div>
                <Label>{t.fields.website}</Label>
                <Input value={website} onChange={(event) => setWebsite(event.target.value)} />
              </div>
              <div>
                <Label>{t.fields.email}</Label>
                <Input
                  value={email}
                  type="email"
                  required
                  onChange={(event) => setEmail(event.target.value.toLowerCase().trim())}
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t.fields.address}</Label>
                <Textarea
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <LabeledCheckbox
                  id="require-evolution"
                  label={t.fields.requireEvolution}
                  checked={requireEvolution}
                  onChange={setRequireEvolution}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => navigate("/app/settings")}>
                {t.cancel}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? t.saving : t.save}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
